import { memo, useState, useEffect, ChangeEvent } from 'react';

import SpectralMaskSettingsForm from './SpectralMaskSettingsForm';
import SpectralMaskList from './SpectralMaskList';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';

import {
  freqValue,
  freqUnit,
  getFrequencyFromSnapshotIndex,
  getIndexFromSnapshotFrequency,
  getCenterWithLo,
} from '../../utils/FreqUtils';

import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Grid,
  SelectChangeEvent,
} from '@mui/material';

import GreenButton from '../../components/GreenButton';
import { InvalidEntryFlags, SpectralMaskForm } from './types';
import { UpdateSnapshotSpectralMask } from '../spectralMasks/types';
import InfoPopup from '../../components/InfoPopup';

import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import { useCreateMask } from 'features/spectralMasks/api/createMask';
import { useUpdateMask } from 'features/spectralMasks/api/updateMask';
import { useDeleteMask } from 'features/spectralMasks/api/deleteMask';
import { useMask } from 'features/spectralMasks/api/getMask';
import { InterferenceDetector } from 'types/shared/InterferenceDetector';
import DraggablePaper from 'components/DraggablePaper';

interface Props {
  traceLength: number | false;
  open: boolean;
  handleClose: () => void;
  handleRedrawMask: (
    maskId: number,
    detectorType: InterferenceDetector,
    detectorName?: string | undefined
  ) => void;
  selectedSnapshotId: number;
  isHistoryMode?: boolean;
}

const invalidEntryFlagsInitialState: InvalidEntryFlags = {
  begin: false,
  end: false,
  upperbound: false,
  lowerbound: false,
};

const MaskConfigurationDialog = memo(
  ({
    traceLength,
    open,
    handleClose,
    handleRedrawMask,
    selectedSnapshotId,
    isHistoryMode = false,
  }: Props) => {
    const [hasChanges, setHasChanges] = useState(false);

    const [selectedMaskId, setSelectedMaskId] = useState(-1);
    const [mask, setMask] = useState<SpectralMaskForm>({
      name: '',
      begin: 0,
      end: 0,
      beginFrequencyValue: '',
      beginFrequencyUnit: 'MHz',
      endFrequencyValue: '',
      endFrequencyUnit: 'MHz',
      upperbound: '',
      lowerbound: '',
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [invalidEntryFlags, setInvalidEntryFlags] =
      useState<InvalidEntryFlags>(invalidEntryFlagsInitialState);

    const { data: snapshot } = useSnapshot({ snapshotId: selectedSnapshotId });
    const { masks } = snapshot || {};
    const { data: selectedMask } = useMask({
      snapshotId: selectedSnapshotId,
      maskId: selectedMaskId,
    });
    const createMaskMutation = useCreateMask({
      snapshotId: selectedSnapshotId,
    });
    const updateMaskMutation = useUpdateMask({
      snapshotId: selectedSnapshotId,
    });
    const deleteMaskMutation = useDeleteMask({
      snapshotId: selectedSnapshotId,
    });

    useEffect(() => {
      if (!masks || masks?.length === 0) {
        setSelectedMaskId(-1);
        return;
      }
      setSelectedMaskId(masks[masks.length - 1].id);
    }, [masks]);

    useEffect(() => {
      if (
        selectedMaskId === -1 ||
        !selectedMask ||
        masks === undefined ||
        masks.length === 0
      ) {
        setMask({
          name: '',
          begin: 0,
          end: 0,
          beginFrequencyValue: '',
          beginFrequencyUnit: 'MHz',
          endFrequencyValue: '',
          endFrequencyUnit: 'MHz',
          upperbound: '',
          lowerbound: '',
        });
        return;
      }

      if (!traceLength || !snapshot) return;

      const beginFrequencyIndex = selectedMask.begin;
      const endFrequencyIndex = selectedMask.end;
      const centerWithLo = getCenterWithLo(
        snapshot.center,
        snapshot.lo,
        snapshot.lo_mode
      );

      const beginFrequency = getFrequencyFromSnapshotIndex(
        centerWithLo,
        snapshot.span,
        traceLength,
        beginFrequencyIndex
      );
      const endFrequency = getFrequencyFromSnapshotIndex(
        centerWithLo,
        snapshot.span,
        traceLength,
        endFrequencyIndex
      );

      const beginFrequencyValue = freqValue(beginFrequency);
      const beginFrequencyUnit = freqUnit(beginFrequency);

      const endFrequencyValue = freqValue(endFrequency);
      const endFrequencyUnit = freqUnit(endFrequency);

      const parsedMask = {
        ...selectedMask,
        beginFrequencyValue: beginFrequencyValue,
        beginFrequencyUnit: beginFrequencyUnit,
        endFrequencyValue: endFrequencyValue,
        endFrequencyUnit: endFrequencyUnit,
        upperbound: selectedMask.upperbound / 1000,
        lowerbound: selectedMask.lowerbound / 1000,
      };

      setMask(parsedMask);
    }, [selectedMask, selectedMaskId, traceLength, snapshot, masks]);

    const handleMaskChanged = (
      event:
        | SelectChangeEvent<string>
        | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const { name, value } = event.target;
      if (name === undefined) return;

      setMask({
        ...mask,
        [name]: value,
      });
      setInvalidEntryFlags(invalidEntryFlagsInitialState);
      setHasChanges(true);
    };

    const handleSubmitClicked = async () => {
      if (!snapshot || traceLength === false) return;

      const beginFrequency =
        mask.beginFrequencyValue + ' ' + mask.beginFrequencyUnit;
      const endFrequency = mask.endFrequencyValue + ' ' + mask.endFrequencyUnit;
      const centerWithLo = getCenterWithLo(
        snapshot.center,
        snapshot.lo,
        snapshot.lo_mode
      );

      const beginIndex = getIndexFromSnapshotFrequency(
        centerWithLo,
        snapshot.span,
        traceLength,
        beginFrequency
      );
      const endIndex = getIndexFromSnapshotFrequency(
        centerWithLo,
        snapshot.span,
        traceLength,
        endFrequency
      );

      // Validate inputs and set error flags accordingly
      const newFlags: InvalidEntryFlags = {
        begin: false,
        end: false,
        upperbound: false,
        lowerbound: false,
      };
      if (beginIndex === -1) newFlags.begin = true;
      if (endIndex === -1) newFlags.end = true;
      if (mask.upperbound < 1) newFlags.upperbound = true;
      if (mask.lowerbound < 1) newFlags.lowerbound = true;
      if (
        Object.keys(newFlags).some(
          (key) =>
            newFlags[key as 'begin' | 'end' | 'upperbound' | 'lowerbound']
        )
      ) {
        setInvalidEntryFlags(newFlags);
        return;
      }

      if (mask.id === undefined || mask.snapshot_id === undefined) return;

      const updatedMask: UpdateSnapshotSpectralMask = {
        id: mask.id,
        snapshot_id: snapshot.id,
        name: mask.name,
        begin: beginIndex,
        end: endIndex,
        upperbound: Number(mask.upperbound) * 1000,
        lowerbound: Number(mask.lowerbound) * 1000,
      };

      await updateMaskMutation.mutateAsync({
        data: updatedMask,
        snapshotId: selectedSnapshotId,
        maskId: updatedMask.id,
      });

      setSelectedMaskId(-1);
      setHasChanges(false);
    };

    const handleDeleteMaskClicked = async () => {
      if (!selectedMask) return;
      await deleteMaskMutation.mutateAsync({
        snapshotId: selectedSnapshotId,
        maskId: selectedMask.id,
      });

      setSelectedMaskId(-1);
    };

    const handleNewMaskClicked = async () => {
      if (!snapshot) return;
      const newMask = {
        snapshot_id: snapshot.id,
        name: 'New Mask',
        begin: 0,
        end: 0,
        upperbound: 10 * 1000,
        lowerbound: 10 * 1000,
      };

      await createMaskMutation.mutateAsync({
        data: newMask,
        snapshotId: selectedSnapshotId,
      });
    };

    const handleMaskClicked = (maskId: number) => {
      setSelectedMaskId(maskId);
      setInvalidEntryFlags(invalidEntryFlagsInitialState);
    };

    return (
      <>
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          itemToDelete={mask.name}
          onSubmit={handleDeleteMaskClicked}
          onClose={() => setDeleteDialogOpen(false)}
        />
        <Dialog
          open={open}
          onClose={handleClose}
          PaperComponent={DraggablePaper}
          hideBackdrop
          aria-labelledby='draggable-dialog-title'
        >
          <DialogTitle style={{ cursor: 'move' }} id='draggable-dialog-title'>
            Mask Configuration
          </DialogTitle>
          <DialogContent>
            <Grid container>
              <Grid item xs={6}>
                <SpectralMaskList
                  masks={masks}
                  selectedMaskId={selectedMaskId}
                  handleMaskClicked={handleMaskClicked}
                />
              </Grid>
              <Grid item xs={6}>
                <SpectralMaskSettingsForm
                  mask={mask}
                  handleMaskChanged={handleMaskChanged}
                  invalidEntryFlags={invalidEntryFlags}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setSelectedMaskId(-1);
                setInvalidEntryFlags(invalidEntryFlagsInitialState);
                handleClose();
              }}
              variant='contained'
              color='secondary'
            >
              Close
            </Button>
            <Button
              onClick={() => setDeleteDialogOpen(true)}
              color='error'
              disabled={selectedMaskId === -1}
              variant='contained'
            >
              Delete Mask
            </Button>
            <Button
              onClick={() =>
                handleRedrawMask(
                  selectedMaskId,
                  InterferenceDetector.Mask,
                  selectedMask?.name
                )
              }
              name='Redraw'
              color='primary'
              variant='contained'
              disabled={selectedMaskId === -1}
            >
              Redraw
            </Button>
            <GreenButton
              onClick={handleNewMaskClicked}
              name='New Mask'
            ></GreenButton>
            <Button
              onClick={handleSubmitClicked}
              color='primary'
              disabled={selectedMaskId === -1 || !hasChanges}
              variant='contained'
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
        <InfoPopup
          openCondition={invalidEntryFlags.begin || invalidEntryFlags.end}
          title='Invalid Values'
          message='Your settings could not be saved due to an invalid begin or end value.'
          error
        />
      </>
    );
  }
);

export default MaskConfigurationDialog;
